/**
 * Admin Navigation — Default Tree (Feb 2026, Board mandate).
 * ────────────────────────────────────────────────────────────────
 * This file is the *single source of truth* for the admin sidebar's
 * default layout. The `DashboardLayout` reads this file at mount and
 * merges any DB overrides (from `GET /api/admin/navigation`) into it
 * before rendering — so hiding / renaming / reordering items no
 * longer requires editing code.
 *
 * Schema:
 *   {
 *     icon: LucideIcon,
 *     label: { ar: '…', en: '…' },
 *     path?: '/route',            // absent when the item is a parent
 *     perm?: 'can_...' | Array,   // optional permission key(s)
 *     group?: 'academic'|'operations'|...,
 *     children?: [<same shape>],  // nested submenu items
 *     external?: boolean,         // opens in new tab (e.g. sandbox)
 *   }
 *
 * Nested items (parents with `children`) are rendered as expandable
 * rows inside their group. Parents themselves have no `path`.
 */
import {
  LayoutDashboard, Users, GraduationCap, Inbox, DollarSign, School,
  ClipboardCheck, Activity, BarChart3, LifeBuoy, Megaphone, BookOpen,
  CalendarX, Trophy, Settings, SlidersHorizontal, Mic,
  Eye, ClipboardList, Palette, ListTree, Shield, CalendarRange, Gauge,
  GitMerge, Wallet, Sparkles, NotebookPen, HeartPulse, Gift, MessageCircle, BellRing, CreditCard, Smartphone, Video,
  Star, Bot, Award,
} from 'lucide-react';

/**
 * Build the default admin menu tree. Icons are Lucide components;
 * labels are bilingual objects so overrides can update either
 * language independently.
 */
export const buildAdminMenu = () => [
  { icon: LayoutDashboard, label: { en: 'Dashboard',        ar: 'لوحة التحكم' },        path: '/dashboard',           group: 'home' },
  { icon: Inbox,           label: { en: 'Inbox & Requests', ar: 'الوارد والطلبات' },     path: '/inbox',               perm: 'can_manage_users',              group: 'people' },
  { icon: Users,           label: { en: 'Students',         ar: 'الطلاب' },              path: '/students',            perm: 'can_access_students',           group: 'people' },
  { icon: GitMerge,        label: { en: 'Duplicate Profiles Merge', ar: 'دمج الملفات المكررة' }, path: '/admin/duplicate-profiles', perm: ['Super_Admin'], group: 'people' },

  // ─── Academic group ────────────────────────────────────────────
  { icon: GraduationCap, label: { en: 'Classrooms & Placement', ar: 'القاعات والتوزيع' }, path: '/academic-hub', perm: 'can_access_classrooms', group: 'academic' },
  { icon: ClipboardCheck, label: { en: 'Assessments', ar: 'الاختبارات' },                 path: '/assessments',   perm: 'can_access_classrooms', group: 'academic' },
  { icon: CalendarRange,  label: { en: 'Shift Coverage', ar: 'تغطية المناوبات' },        path: '/admin/shifts',  perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: GraduationCap,  label: { en: 'Teacher Applications', ar: 'طلبات التوظيف' },    path: '/admin/teacher-applications', perm: ['Super_Admin', 'Admin', 'Academic_Supervisor'], group: 'people' },
  { icon: Gauge,          label: { en: 'Classroom Monitoring Center', ar: 'مركز مراقبة القاعات' }, path: '/admin/classroom-monitor', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: BookOpen,       label: { en: 'Academic Progress Updates', ar: 'متابعة تحديث التقدم' }, path: '/admin/academic-progress-monitor', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: Star,           label: { en: 'Lesson Feedback', ar: 'تقييم الاستفادة من الحصص' },        path: '/admin/lesson-feedback',   perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: Video,          label: { en: 'Class Recordings', ar: 'تسجيلات الحصص' },                 path: '/admin/class-recordings',  perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: Award,          label: { en: 'Level Certificates', ar: 'شهادات اجتياز المستويات' },      path: '/admin/certificates',      perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: Sparkles,       label: { en: 'Enrichment Monitor', ar: 'مراقبة المواد الإثرائية' },      path: '/admin/enrichment-monitor', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: NotebookPen,    label: { en: 'Lesson Preparations', ar: 'تحضيرات المعلمين' },            path: '/admin/lesson-preps',       perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },
  { icon: Sparkles,       label: { en: 'Teacher Question Contributions', ar: 'مساهمات المعلمين — بنك الأسئلة' }, path: '/admin/question-contributions', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'academic' },

  // Nested parent: Placement V3 Operations (Feb 2026)
  {
    icon: SlidersHorizontal,
    label: { en: 'Placement V3 Ops', ar: 'عمليات تحديد المستوى V3' },
    group: 'academic',
    perm: ['Super_Admin', 'Academic_Supervisor'],
    children: [
      { icon: Settings, label: { en: 'V3 Settings & Coverage', ar: 'الإعدادات ومصفوفة التغطية' }, path: '/admin/placement-v3-settings', perm: ['Super_Admin', 'Academic_Supervisor'] },
      { icon: Mic,      label: { en: 'Pending Audio Media',   ar: 'الوسائط الصوتية المعلّقة' },   path: '/admin/pending-media',          perm: ['Super_Admin', 'Academic_Supervisor'] },
      { icon: Palette,  label: { en: 'Theme Sandbox',         ar: 'معاينة الثيم' },              path: '/preview/placement-v3-theme',   perm: ['Super_Admin', 'Academic_Supervisor'], external: true },
    ],
  },

  // Nested parent: Placement V2 Operations (legacy, dormant tools)
  {
    icon: ClipboardList,
    label: { en: 'Placement V2 Ops (Legacy)', ar: 'عمليات V2 (أرشيف)' },
    group: 'academic',
    perm: ['Super_Admin', 'Academic_Supervisor'],
    children: [
      { icon: Eye,             label: { en: 'V2 Preview (Dry-Run)', ar: 'معاينة V2 (تجريبية)' }, path: '/admin/placement-preview', perm: ['Super_Admin', 'Academic_Supervisor'] },
      { icon: ClipboardCheck,  label: { en: 'Match Review',          ar: 'مراجعة التوزيع' },    path: '/admin/match-review',      perm: ['Super_Admin', 'Academic_Supervisor'] },
    ],
  },

  { icon: Activity,   label: { en: 'Daily Operations',    ar: 'العمليات اليومية' },     path: '/operations-hub',     perm: 'can_access_students',           group: 'operations' },
  { icon: DollarSign, label: { en: 'Finance',              ar: 'المالية' },              path: '/finance-hub',        perm: 'can_access_finance',            group: 'finance' },
  { icon: Wallet,     label: { en: 'Teacher Dues',         ar: 'مستحقات المعلمين' },     path: '/admin/teacher-dues', perm: ['Super_Admin'],                 group: 'finance' },
  { icon: Gift,       label: { en: 'Referral Program',     ar: 'نظام الإحالات' },        path: '/admin/referrals',    perm: 'can_access_finance',            group: 'finance' },
  { icon: CreditCard, label: { en: 'Card Vault',           ar: 'مخزن البطاقات' },        path: '/admin/recharge-cards', perm: ['Super_Admin', 'Financial_Manager'], group: 'finance' },
  { icon: Wallet,     label: { en: 'Ambiguous Sessions',   ar: 'جلسات للمراجعة' },       path: '/admin/ambiguous-sessions', perm: ['Super_Admin'],             group: 'finance' },
  { icon: Wallet,     label: { en: 'Dues Review Requests', ar: 'طلبات مراجعة المستحقات' }, path: '/admin/dues-reviews', perm: ['Super_Admin'],                 group: 'finance' },
  { icon: BarChart3,  label: { en: 'Reports & Analytics',  ar: 'التقارير والتحليلات' }, path: '/reports-hub',        perm: 'can_access_reports',            group: 'reports' },
  { icon: Bot,        label: { en: 'Academic Assistant',   ar: 'المساعد الأكاديمي' },    path: '/admin/academic-assistant', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'reports' },
  { icon: Gauge,      label: { en: 'MESP CORE',            ar: 'مركز عمليات MESP' },     path: '/admin/mesp-core',       perm: ['Super_Admin'],              group: 'reports' },
  { icon: HeartPulse, label: { en: 'Health Monitor',       ar: 'مراقبة صحة النظام' },    path: '/admin/health-monitor',  perm: ['Super_Admin'],              group: 'reports' },
  { icon: Smartphone, label: { en: 'App Adoption',         ar: 'متابعة تثبيت التطبيق' }, path: '/admin/platform-adoption', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'reports' },

  { icon: Users,       label: { en: 'Users & Roles',       ar: 'المستخدمون والصلاحيات' }, path: '/users',              perm: 'can_manage_users',              group: 'support' },
  { icon: Users,       label: { en: 'Parent Relations',    ar: 'علاقات أولياء الأمور' },  path: '/parent-relations',   perm: ['Super_Admin', 'Academic_Supervisor', 'Registration_Officer'], group: 'people' },
  { icon: BookOpen,    label: { en: 'Academic Programs',   ar: 'البرامج الأكاديمية' },   path: '/academic',           perm: 'can_manage_academic_structure', group: 'support' },
  { icon: School,      label: { en: 'Academic Structure',  ar: 'الهيكل الأكاديمي' },    path: '/academic-structure', perm: 'can_manage_academic_structure', group: 'support' },
  { icon: BookOpen,    label: { en: 'Content Library CMS', ar: 'إدارة المحتوى والمكتبة' }, path: '/content-library',  perm: 'can_manage_academic_structure', group: 'support' },
  { icon: LifeBuoy,    label: { en: 'Support Inbox',       ar: 'صندوق الدعم' },          path: '/support/inbox',      perm: 'can_manage_users',              group: 'support' },
  { icon: Megaphone,   label: { en: 'Communications Hub', ar: 'مركز التواصل والإعلانات' }, path: '/admin/communications', perm: 'can_manage_users',           group: 'support' },
  { icon: MessageCircle, label: { en: 'WhatsApp Communities', ar: 'مجتمعات واتساب' }, path: '/admin/whatsapp-communities', perm: ['Super_Admin', 'Academic_Supervisor'], group: 'support' },
  { icon: BellRing, label: { en: 'Paging Monitor', ar: 'مراقبة النداءات' }, path: '/admin/paging-monitor', perm: ['Super_Admin', 'Academic_Supervisor', 'Admin'], group: 'support' },
  { icon: ListTree,    label: { en: 'Navigation Editor',   ar: 'تحرير القائمة الجانبية' }, path: '/admin/navigation', perm: ['Super_Admin'],                group: 'support' },
  { icon: Shield,      label: { en: 'Data Quality',        ar: 'جودة البيانات' },        path: '/admin/data-quality',  perm: ['Super_Admin'],                group: 'support', badge: 'dq' },
];

/**
 * Merge server-provided overrides into a default menu tree, in place
 * of any matching `path`. Overrides can:
 *   • hide an item        (`hidden: true`)
 *   • rename it           (`label_ar` / `label_en`)
 *   • reorder its sibling (`order: <int>`)
 *
 * Sorting is stable within siblings sharing the same order value,
 * so items with no explicit order keep their file-declared sequence.
 * Recursion covers nested `children` so parents and children are
 * both configurable from the same overrides doc.
 */
export const applyNavOverrides = (items, overrides = {}) => {
  const walk = (arr) => {
    const decorated = arr
      .map((it, idx) => {
        const ov = it.path ? overrides[it.path] : null;
        if (ov?.hidden === true) return null;
        const label = {
          ar: ov?.label_ar || it.label?.ar,
          en: ov?.label_en || it.label?.en,
        };
        const nextChildren = Array.isArray(it.children) ? walk(it.children) : undefined;
        // Hide a parent whose entire subtree collapsed to zero.
        if (nextChildren && nextChildren.length === 0) return null;
        return {
          ...it,
          label,
          children: nextChildren,
          _order: ov?.order ?? idx,
          _idx: idx,
        };
      })
      .filter(Boolean);
    decorated.sort((a, b) => (a._order - b._order) || (a._idx - b._idx));
    return decorated.map(({ _order, _idx, ...rest }) => rest);
  };
  return walk(items || []);
};

/**
 * Flatten the (possibly nested) menu into a per-role list of {path,
 * label, group} entries — the shape the existing breadcrumb + sidebar
 * consumers already understand. `translate(item.label)` picks the AR
 * or EN string based on the active language.
 */
export const flattenMenuForConsumers = (items, translate) => {
  const out = [];
  const walk = (arr, parent = null) => {
    for (const it of arr) {
      const labelStr = translate ? translate(it.label) : it.label?.en;
      if (Array.isArray(it.children)) {
        walk(it.children, it);
      } else if (it.path) {
        out.push({
          icon: it.icon,
          label: labelStr,
          path: it.path,
          perm: it.perm,
          group: it.group || parent?.group,
          external: it.external === true,
        });
      }
    }
  };
  walk(items);
  return out;
};
